import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useGlobalContext } from '@/store';
import { apiClient } from '@/api/client';

export interface StepItem {
  id: number;
  label: string;
  shortLabel: string;
  path: string;
}

const STEPS: StepItem[] = [
  { id: 1, label: 'Personal Data', shortLabel: 'Step 1: Data', path: '/account' },
  { id: 2, label: 'Case Description', shortLabel: 'Step 2: Description', path: '/description' },
  { id: 3, label: 'Physical Symptoms', shortLabel: 'Step 3: Physical', path: '/physical-symptom' },
  { id: 4, label: 'Mental Symptoms', shortLabel: 'Step 4: Mental', path: '/mind' },
  { id: 5, label: 'Symptom Selection', shortLabel: 'Step 5: Selection', path: '/intake' },
  { id: 6, label: 'Results', shortLabel: 'Step 6: Results', path: '/results' },
];

export function ConsultationStepper() {
  const { patient } = useGlobalContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const currentStep = STEPS.find((s) => s.path === location.pathname)?.id || 1;

  useEffect(() => {
    if (!patient?.email || !patient?.consultationToken) {
      setCompletedSteps(new Set());
      return;
    }

    const newCompleted = new Set<number>();
    newCompleted.add(1);

    const date0 = new Date().toISOString().slice(0, 10);
    Promise.all([
      apiClient.get('/patients/by-email', {
        params: {
          email: patient.email,
          name: patient.name,
          consultation_token: patient.consultationToken,
        },
      }),
      apiClient.get('/patients/symptoms', {
        params: {
          email: patient.email,
          date_0: date0,
          consultation_token: patient.consultationToken,
        },
      }),
    ])
      .then(([patientRes, symptomsRes]) => {
        const pData = patientRes.data;
        const symptoms: Array<{ step: number }> = symptomsRes.data?.symptoms || [];

        let descText = '';
        if (typeof pData?.description === 'string') {
          descText = pData.description;
        } else if (Array.isArray(pData?.description)) {
          descText = pData.description.join(' ');
        }

        if (descText.trim() || symptoms.some((s) => s.step === 0 || s.step === 1 || s.step === 2)) {
          newCompleted.add(2);
        }

        symptoms.forEach((s) => {
          if (s.step === 3) newCompleted.add(3);
          if (s.step === 4) newCompleted.add(4);
          if (s.step === 5) newCompleted.add(5);
        });

        if (pData?.validated || pData?.remedy_validated || pData?.validated_at) {
          newCompleted.add(6);
        }

        setCompletedSteps(newCompleted);
      })
      .catch(() => {
        setCompletedSteps(newCompleted);
      });
  }, [patient?.email, patient?.name, patient?.consultationToken, location.pathname]);

  const completedCount = Array.from(completedSteps).filter((id) => id >= 1 && id <= 6).length;

  const handleStepClick = (step: StepItem) => {
    if (step.id <= currentStep || completedSteps.has(step.id) || completedSteps.has(step.id - 1)) {
      navigate(step.path);
    }
  };

  return (
    <div className="w-full bg-base-100 border border-base-200 rounded-xl p-3 md:p-4 shadow-sm mb-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-1 border-b border-base-200 pb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-base-content/80">Consultation Flow</span>
          <span className="badge badge-primary badge-outline font-medium text-xs">
            Completed stages: {completedCount} / 6
          </span>
        </div>
        <div className="text-xs text-base-content/60 flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-success inline-block"></span> Completed
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-primary inline-block"></span> In progress
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-base-300 inline-block"></span> Pending
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
        {STEPS.map((step) => {
          const isCurrent = step.id === currentStep;
          const isDone = completedSteps.has(step.id);
          const isReadOnly = completedSteps.has(6);

          let isClickable = step.id <= currentStep || isDone || completedSteps.has(step.id - 1);
          if (isReadOnly && step.id < 6) {
            isClickable = false;
          }

          let bgClass = 'bg-base-200 text-base-content/50 border-base-300';
          let badgeClass = 'bg-base-300 text-base-content/60';

          if (isReadOnly && step.id < 6) {
            bgClass = 'bg-base-200/50 text-base-content/30 border-base-200 opacity-40 cursor-not-allowed pointer-events-none';
            badgeClass = 'bg-base-300/40 text-base-content/40';
          } else if (isCurrent) {
            bgClass = 'bg-primary text-primary-content font-bold border-primary shadow-sm ring-2 ring-primary/30';
            badgeClass = 'bg-primary-content text-primary';
          } else if (isDone) {
            bgClass = 'bg-success/15 text-success border-success/40 hover:bg-success/25 cursor-pointer';
            badgeClass = 'bg-success text-success-content';
          } else if (isClickable) {
            bgClass = 'bg-base-100 text-base-content border-base-300 hover:bg-base-200 cursor-pointer';
            badgeClass = 'bg-base-200 text-base-content';
          }

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => handleStepClick(step)}
              disabled={!isClickable}
              className={`flex items-center justify-between p-2.5 rounded-lg border text-left transition-all text-xs font-medium ${bgClass}`}
            >
              <div className="truncate pr-1">
                <span className="block text-[11px] opacity-80 font-normal">Step {step.id}</span>
                <span className="truncate block font-semibold">{step.label}</span>
              </div>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] shrink-0 ${badgeClass}`}>
                {isDone && !isCurrent ? <Check size={12} strokeWidth={3} /> : step.id}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
